import { useForm } from 'react-hook-form';
import { resolveApiError, useTranslation } from '@eon/i18n';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  SelectField,
  toast,
} from '@eon/ui';
import { hooks } from '@eon/api-client';
import { isValidTurkishMobile, normalizeTurkishPhone } from '@eon/utils';

const MEMBER_ROLES = ['owner', 'admin', 'manager', 'member', 'viewer'] as const;

interface InviteForm {
  phoneNumber: string;
  role: string;
}

interface InviteGroupMemberDialogProps {
  groupId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

// Invitations are SMS-only for now: the e-mail provider is unavailable, so
// the e-mail field is hidden until delivery is restored.
export function InviteGroupMemberDialog({
  groupId,
  open,
  onOpenChange,
}: InviteGroupMemberDialogProps) {
  const { t } = useTranslation();
  const invite = hooks.useCreateGroupInvitation();
  const { register, handleSubmit, setValue, watch, formState, reset } = useForm<InviteForm>({
    defaultValues: { phoneNumber: '', role: 'member' },
  });
  const role = watch('role');

  const roleOptions = MEMBER_ROLES.map((r) => ({
    value: r,
    label: t(`group.role.${r}`),
  }));

  const onPhoneBlur = (value: string) => {
    const normalized = normalizeTurkishPhone(value);
    if (normalized !== value) setValue('phoneNumber', normalized);
  };

  const onSubmit = handleSubmit(async (values) => {
    const phone = normalizeTurkishPhone(values.phoneNumber);
    if (!phone) {
      toast.error(t('group.invite.phoneRequired'));
      return;
    }
    if (!isValidTurkishMobile(phone)) {
      toast.error(t('group.invite.phoneInvalid'));
      return;
    }
    try {
      await invite.mutateAsync({
        groupId,
        phoneNumber: phone,
        role: values.role,
      });
      toast.success(t('group.invite.sent'));
      reset();
      onOpenChange(false);
    } catch (err) {
      toast.error(resolveApiError(err, t));
    }
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('group.invite.title')}</DialogTitle>
          <DialogDescription>{t('group.invite.descriptionSms')}</DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="phone">{t('group.invite.phone')}</Label>
            <Input
              id="phone"
              type="tel"
              inputMode="tel"
              placeholder="+905xxxxxxxxx"
              {...register('phoneNumber', {
                onBlur: (e) => onPhoneBlur(e.target.value),
              })}
            />
            <p className="text-muted-foreground text-xs">{t('group.invite.emailDisabled')}</p>
          </div>
          <div className="space-y-1.5">
            <Label>{t('group.role.label')}</Label>
            <SelectField value={role} onValueChange={(v) => setValue('role', v)} options={roleOptions} />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t('actions.cancel')}
            </Button>
            <Button type="submit" disabled={formState.isSubmitting || invite.isPending}>
              {t('group.invite.send')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
